<template>
  <page-wrapper ref="pageEl" :title="title">
    <loading-overlay :active="loading"></loading-overlay>
    <div class="wrapper">
      <div class="row mb-2">
        <div class="col-2">
          <div class="label mb-2">ประเภททรัพย์สิน</div>
          <sell-and-rent-source-select
            v-model="searchForm.source"
            class="w-100"
          ></sell-and-rent-source-select>
        </div>
        <div class="col-2">
          <div class="label mb-2">ชื่อ</div>
          <el-input v-model="searchForm.name" />
        </div>
        <div class="col-2">
          <div class="label mb-2">สถานะ</div>
          <sell-and-rent-status-select
            v-model="searchForm.status"
            for-filter
            class="w-100"
          ></sell-and-rent-status-select>
        </div>
        <div class="col-4">
          <div class="label mb-2">วันที่ยื่นคำขอ</div>
          <date-picker
            v-model="searchForm.createdAt"
            type="datetimerange"
          ></date-picker>
        </div>
        <div class="col-2">
          <div class="label mb-2">เรียงตาม</div>
          <el-select
            v-model="orderBy"
            class="w-100"
            placeholder="วันที่ยื่นคำขอ"
            clearable
          >
            <el-option
              v-for="item in orderBySelect"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            ></el-option>
          </el-select>
        </div>
      </div>
      <div class="text-end">
        <sc-button variant="primary" @click="handleClickSearch"
          >ค้นหา</sc-button
        >
      </div>
      <p v-if="forms.length" class="row mb-2 mt-2">{{ totalCount }}</p>
      <sc-button variant="secondary" @click="handleClickDownloadReport"
        >Download Report</sc-button
      >
    </div>
    <div class="list-scroll">
      <div class="row-header">
        <div class="cell">ลูกค้า</div>
        <div class="cell">เบอร์โทร</div>
        <div class="cell">ประเภททรัพย์สิน</div>
        <div class="cell">บ้านเลขที่</div>
        <div class="cell wrap">โครงการ</div>
        <div class="cell">เลขแปลง</div>
        <div class="cell wrap">ที่อยู่ (นอกระบบ)</div>
        <div class="cell currency">เนื้อที่ใช้สอย</div>
        <div class="cell currency">ราคาฝากขาย</div>
        <div class="cell currency">ราคาปล่อยเช่า</div>
        <div class="cell">วันที่ยื่นคำขอ</div>
        <div class="cell">อัพเดทโดย</div>
        <div class="cell wrap">สถานะ</div>
      </div>
      <sell-and-rent-row
        v-for="(item, index) in formsPresenter"
        :key="index"
        v-bind="item"
      ></sell-and-rent-row>
    </div>
    <div class="wrapper text-end">
      <el-pagination
        :total="total"
        :page-size="10"
        :current-page="queryParams.page"
        background
        layout="prev, pager, next"
        @current-change="handleCurrentChange"
      ></el-pagination>
    </div>
  </page-wrapper>
</template>

<script>
import { baanRueJaiClient } from "@/dependencies"
import { Input, Option, Pagination, Select } from "element-ui"
import { DateTime } from "luxon"
import Vue from "vue"

import DatePicker from "@/lib/components/DatePicker"
import PageWrapper from "@/lib/components/PageWrapper"
import ScButton from "@/lib/components/ScButton"
import LoadingOverlay from "@/lib/components/LoadingOverlay"

import {
  SellAndRentStatus,
  getSellAndRentStatusLabel,
} from "../SellAndRentStatus"
import SellAndRentRow from "../components/SellAndRentRow"
import SellAndRentStatusSelect from "../components/SellAndRentStatusSelect"
import SellAndRentSourceSelect from "../components/SellAndRentSourceSelect"

export default Vue.extend({
  components: {
    PageWrapper,
    SellAndRentRow,
    ScButton,
    LoadingOverlay,
    SellAndRentStatusSelect,
    SellAndRentSourceSelect,
    "el-input": Input,
    "el-pagination": Pagination,
    "el-select": Select,
    "el-option": Option,
    DatePicker,
  },
  data() {
    const title = "รายการฝากขายปล่อยเช่า"
    const forms = []
    const total = 0
    const loading = false
    const searchForm = {
      name: null,
      status: null,
      source: "",
      createdAt: [],
    }
    const orderBy = ""
    const orderBySelect = [
      { value: "created_at", label: "วันที่ยื่นคำขอ" },
      { value: "updated_at", label: "วันที่อัพเดทล่าสุด" },
    ]
    return { title, forms, total, loading, searchForm, orderBy, orderBySelect, formsRequestSeq: 0 }
  },
  computed: {
    totalCount() {
      const limit = 10
      const { total } = this
      const currentTotalItem = this.forms.length
      const isLastPage = limit > currentTotalItem
      const currentPage = this.queryParams.page
      const end = isLastPage ? total : currentPage * limit
      const start = (isLastPage ? limit * currentPage - limit : end - limit) + 1

      return `แสดงผล ${start.toLocaleString()} - ${end.toLocaleString()} จากทั้งหมด ${total.toLocaleString()}`
    },
    formsPresenter() {
      return this.forms.map((form) => {
        const { id, user, home, source } = form
        const route = { name: "SellAndRentDetailPage", params: { id } }

        let status = getSellAndRentStatusLabel(
          form.status,
          form.cancelled_by_admin_id,
        )

        if (
          status === SellAndRentStatus.WAITING_FOR_AUTHORITY &&
          form.is_resubmitted
        ) {
          status = `${status} (ลูกค้าแก้ไขข้อมูล)`
        }

        // NOTE: Vue camelizes kebab-case attributes only, so these keys must match SellAndRentRow's props verbatim.
        return {
          id,
          user,
          home,
          source,
          status,
          route,
          created_at: form.created_at,
          updated_by: form.updated_by ?? { first_name: "" },
        }
      })
    },
    queryParams() {
      return {
        contactName: this.$route.query.name,
        page: parseInt(this.$route.query.page, 10) || 1,
        status: this.$route.query.status,
        source: this.$route.query.source,
        createdAtStart: this.$route.query.createdAtStart,
        createdAtEnd: this.$route.query.createdAtEnd,
        orderBy: this.$route.query.orderBy,
      }
    },
  },
  watch: {
    "$route.query": async function watchRouteQuery() {
      await this.setupData()

      this.$refs.pageEl.$el.querySelector(".page-content").scrollTop = 0
    },
  },
  created() {
    this.initPage()
  },
  methods: {
    initPage() {
      this.setupSearchForm()
      this.setupData()
    },
    setupSearchForm() {
      this.searchForm = {
        name: this.queryParams.contactName,
        status: this.queryParams.status,
        source: this.queryParams.source || "",
        createdAt:
          this.queryParams.createdAtStart && this.queryParams.createdAtEnd
            ? [
                DateTime.fromISO(this.queryParams.createdAtStart),
                DateTime.fromISO(this.queryParams.createdAtEnd),
              ]
            : [],
      }
      this.orderBy = this.queryParams.orderBy
    },
    async setupData() {
      await this.setupForms()
    },
    async setupForms() {
      this.loading = true
      this.formsRequestSeq += 1
      const seq = this.formsRequestSeq

      try {
        const { count, sell_and_rent_forms: form } = await this.fetchForms()
        if (seq !== this.formsRequestSeq) return

        this.forms = form
        this.total = count
      } finally {
        this.loading = false
      }
    },
    async fetchForms() {
      const params = this.queryParams
      const response =
        await baanRueJaiClient.admin.homes.sellAndRent.listForms(params)

      return response.data.result
    },
    handleCurrentChange(page) {
      const route = {
        name: this.$route.name,
        query: { ...this.$route.query, page },
      }

      this.$router.replace(route)
    },
    handleClickSearch() {
      const { name, status, source, createdAt } = this.searchForm
      const [createdAtStart, createdAtEnd] = createdAt

      const route = {
        name: this.$route.name,
        query: {
          ...this.$route.query,
          createdAtStart: createdAtStart && createdAtStart.toISO(),
          createdAtEnd: createdAtEnd && createdAtEnd.toISO(),
          name,
          status,
          source,
          orderBy: this.orderBy,
          page: 1,
        },
      }

      this.$router.replace(route)
    },
    async handleClickDownloadReport() {
      this.loading = true

      try {
        const response = await baanRueJaiClient.admin.homes.sellAndRent.getReport(
          this.queryParams,
        )

        const blob = new Blob([response.data])
        const link = document.createElement("a")
        link.href = window.URL.createObjectURL(blob)
        link.download = `list-sell-and-rent.xlsx`
        link.click()
      } catch (error) {
        this.$message.error("ไม่สามารถดาวน์โหลดรายงานได้")
      } finally {
        this.loading = false
      }
    },
  },
})
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

.wrapper {
  padding: ($space / 2) $space;
}

.list-scroll {
  // full-bleed horizontally-scrollable table (no side gutters)
  overflow-x: auto;
}

// Shared column widths — MUST stay identical to .sell-and-rent-row so the
// header and every row line up. Sum exceeds most viewports on purpose so the
// list scrolls horizontally instead of wrapping content.
$sell-and-rent-columns: 150px 120px 150px 100px 160px 100px 180px 120px 130px 130px 130px 130px 185px;

.row-header {
  display: grid;
  grid-template-columns: $sell-and-rent-columns;
  width: max-content;
  min-width: 100%;
  // left padding aligns the first column with the filters; right stays full-bleed
  padding: ($space / 2) 0 ($space / 2) $space;

  grid-gap: 15px;

  border-bottom: $border;

  font-weight: bold;

  > .cell {
    white-space: nowrap;
  }

  // columns allowed to wrap onto multiple lines (โครงการ / ที่อยู่ / สถานะ)
  > .cell.wrap {
    white-space: normal;
    overflow-wrap: break-word;
  }

  > .currency {
    margin-right: 30px;
    text-align: right;
  }
}

.el-date-editor--datetimerange.el-input__inner {
  width: 100%;
}
</style>
